import { Input, Component, inject } from "@angular/core";
import { ImageService } from '@tytapp/common/image-service';

interface Source {
    srcset: string;
    sizes?: string;
    type?: string;
    media?: string;
}

@Component({
    selector: 'responsive-picture',
    template: `
        <picture>
            <source *ngFor="let src of sizes" [attr.srcset]="src.srcset" [attr.media]="src.media" [attr.type]="src.type" />
            <img [attr.src]="legacySrc" [attr.alt]="alt" />
        </picture>
    `,
    styles: [`
        :host {
            display: block;
        }

        picture, img {
            width: 100%;
        }
    `]
})
export class ResponsivePictureComponent {
    private imageService = inject(ImageService);

    private _src: string;

    legacySrc: string;

    @Input()
    alt: string;

    @Input()
    get src(): string {
        return this._src;
    }

    set src(value) {
        this._src = value;
        setTimeout(() => this.setup(value));
    }

    sizes: Source[];

    @Input()
    widthFactor = 1;

    @Input()
    mobileWidthFactor = 1;

    @Input()
    width: number = undefined;

    setSizes(url: string) {
        const sizes = [1920.0, 1280.0, 640.0, 320.0];
        this.sizes = [
            ...sizes.map(width => ({
                srcset: this.imageService.transform(url, { width, format: 'webp' }),
                type: 'image/webp',
                media: `(min-width: ${ width / this.widthFactor }px)`
            })),
            ...sizes.map(width => ({
                srcset: this.imageService.transform(url, { width }),
                media: `(min-width: ${ width / this.widthFactor }px)`
            }))
        ];
    }

    async setup(url: string) {
        if (typeof url !== 'string')
            return;

        if (!this.imageService.canBeProcessed(url)) {
            this.legacySrc = url;
            this.sizes = [{ srcset: url }];
            return;
        }

        if (this.width) {
            this.legacySrc = this.imageService.transform(url, { width: this.width });
            this.sizes = [{ srcset: this.legacySrc }];
        } else {
            this.legacySrc = await this.imageService.resolveImage(url, this.widthFactor, this.mobileWidthFactor);
            this.setSizes(url);
        }
    }
}
